import React, { useState } from "react";
import useSWR from "swr";
import { Table, Modal, Form, Input, message, Popconfirm, Button, Space, Drawer, Descriptions } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getSolutionGroupList,
  createSolutionGroup,
  updateSolutionGroup,
  deleteSolutionGroup,
  toggleSolutionGroupUse,
  getSolutionGroupDetail,
} from "../api/domainApi";
import { SolutionGroupRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;

const SolutionGroupPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SolutionGroupRes | null>(null);
  const [form] = Form.useForm();

  const [detailId, setDetailId] = useState<number | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const swrKey = `/solution-group?page=${page}&kw=${keyword}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () => getSolutionGroupList({ page: page - 1, size: PAGE_SIZE, keyword }),
    { keepPreviousData: true }
  );

  const { data: detail } = useSWR(
    detailId ? `/solution-group/${detailId}/detail` : null,
    () => getSolutionGroupDetail(detailId!)
  );

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: SolutionGroupRes) => {
    setEditing(row);
    form.setFieldsValue({ sltnGrpTitle: row.sltnGrpTitle });
    setModalOpen(true);
  };
  const openDetail = (row: SolutionGroupRes) => { setDetailId(row.id); setDetailDrawerOpen(true); };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await updateSolutionGroup(editing.id, values);
        message.success("수정되었습니다.");
      } else {
        await createSolutionGroup(values);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSolutionGroup(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleToggle = async (row: SolutionGroupRes) => {
    try {
      await toggleSolutionGroupUse(row.id);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<SolutionGroupRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "그룹명", dataIndex: "sltnGrpTitle", ellipsis: { showTitle: true } },
    { title: "솔루션 수", dataIndex: "solutionCount", width: 100 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리",
      width: 210,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>수정</Button>
          <Button size="small" type="dashed" onClick={() => openDetail(row)}>상세</Button>
          <Button size="small" onClick={() => handleToggle(row)}>{row.useYn ? "비활성" : "활성"}</Button>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const detailSolutionColumns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "유형", dataIndex: "sltnType", width: 80 },
    { title: "제목", dataIndex: "sltnTtl", ellipsis: { showTitle: true } },
    { title: "순서", dataIndex: "sortOrder", width: 70 },
    { title: "사용여부", dataIndex: "useYn", width: 90, render: (v: boolean) => <UseYnTag value={v} /> },
  ];

  return (
    <div>
      <PageHeader title="솔루션그룹" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="그룹명 검색"
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => { setKeyword(kwInput); setPage(1); }}
          style={{ width: 220 }}
        />
        <Button onClick={() => { setKeyword(kwInput); setPage(1); }}>검색</Button>
        <Button onClick={() => { setKwInput(""); setKeyword(""); setPage(1); }}>초기화</Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? "솔루션그룹 수정" : "솔루션그룹 추가"}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"}
        cancelText="취소"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="sltnGrpTitle" label="그룹명" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={`솔루션그룹 상세 — ${detail?.sltnGrpTitle ?? ""}`}
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        size={700}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" style={{ marginBottom: 24 }} column={2}>
              <Descriptions.Item label="그룹명">{detail.sltnGrpTitle}</Descriptions.Item>
              <Descriptions.Item label="사용여부"><UseYnTag value={detail.useYn} /></Descriptions.Item>
            </Descriptions>
            <div style={{ marginBottom: 12 }}><strong>솔루션 목록</strong></div>
            <Table
              rowKey="id"
              columns={detailSolutionColumns}
              dataSource={detail.solutionList}
              pagination={false}
              size="small"
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default SolutionGroupPage;
